import { 
  checkDuplicateFAQs, 
  removeDuplicateFAQs, 
  cleanupFAQs,
  getAllFAQs,
  getFAQCategories
} from '../services/faqService';

// Utility per la pulizia FAQ - può essere eseguita dalla console del browser
export const FAQCleanupUtils = {
  // Controlla i doppioni senza rimuoverli
  async checkDuplicates() {
    try {
      console.log('🔍 Controllo doppioni FAQ...');
      const result = await checkDuplicateFAQs();
      
      console.log('📊 Risultati controllo doppioni:');
      console.log(`- Totale FAQ: ${result.totalFAQs}`);
      console.log(`- FAQ uniche: ${result.uniqueFAQs}`);
      console.log(`- Gruppi duplicati: ${result.duplicates.length}`);
      
      if (result.duplicates.length > 0) {
        console.log('\n🚨 Doppioni trovati:');
        result.duplicates.forEach((duplicate, index) => {
          console.log(`\n${index + 1}. ${duplicate.reason}`);
          duplicate.group.forEach((faq, faqIndex) => {
            console.log(`   ${faqIndex + 1}. "${faq.question}" (ID: ${faq.id})`);
          });
        });
      } else {
        console.log('✅ Nessun doppione trovato!');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Errore nel controllo doppioni:', error);
      throw error;
    }
  },

  // Rimuovi solo i doppioni
  async removeDuplicates() {
    try {
      console.log('🗑️ Rimozione doppioni FAQ...');
      const result = await removeDuplicateFAQs();
      
      console.log('✅ Risultati rimozione doppioni:');
      console.log(`- FAQ rimosse: ${result.removed}`);
      console.log(`- FAQ mantenute: ${result.kept}`);
      
      if (result.details.length > 0) {
        console.log('\n📋 Dettagli rimozioni:');
        result.details.forEach((detail, index) => {
          console.log(`\n${index + 1}. ${detail.reason}`);
          console.log(`   Mantenuta: "${detail.kept.question}" (ID: ${detail.kept.id})`);
          detail.removed.forEach((faq, faqIndex) => {
            console.log(`   Rimossa: "${faq.question}" (ID: ${faq.id})`);
          });
        });
      }
      
      return result;
    } catch (error) {
      console.error('❌ Errore nella rimozione doppioni:', error);
      throw error;
    }
  },

  // Pulizia completa (doppioni + ordine)
  async fullCleanup() {
    try {
      console.log('🧹 Pulizia completa FAQ...');
      const result = await cleanupFAQs();
      
      console.log('✅ Risultati pulizia completa:');
      console.log(`- Doppioni rimossi: ${result.duplicatesRemoved}`);
      console.log(`- Ordine sistemato: ${result.orderingFixed} FAQ`);
      console.log(`- Totale FAQ dopo pulizia: ${result.totalFAQs}`);
      
      return result;
    } catch (error) {
      console.error('❌ Errore nella pulizia completa:', error);
      throw error;
    }
  },

  // Mostra statistiche FAQ
  async showStats() {
    try {
      console.log('📈 Statistiche FAQ...');
      const [faqs, categories] = await Promise.all([
        getAllFAQs(),
        getFAQCategories()
      ]);
      
      console.log('📊 Statistiche generali:');
      console.log(`- Totale FAQ: ${faqs.length}`);
      console.log(`- Categorie: ${categories.length}`);
      
      // Statistiche per categoria
      const categoryStats = new Map<string, number>();
      faqs.forEach(faq => {
        const categoryName = categories.find(c => c.id === faq.category)?.name || faq.category;
        categoryStats.set(categoryName, (categoryStats.get(categoryName) || 0) + 1);
      });
      
      console.log('\n📋 FAQ per categoria:');
      categoryStats.forEach((count, category) => {
        console.log(`- ${category}: ${count} FAQ`);
      });
      
      // FAQ attive vs inattive
      const activeCount = faqs.filter(faq => faq.isActive).length;
      const inactiveCount = faqs.length - activeCount;
      
      console.log('\n📊 Stato FAQ:');
      console.log(`- Attive: ${activeCount}`);
      console.log(`- Inattive: ${inactiveCount}`);
      
      return { faqs, categories, categoryStats, activeCount, inactiveCount };
    } catch (error) {
      console.error('❌ Errore nel caricamento statistiche:', error);
      throw error;
    }
  },

  // Reset ordine FAQ per categoria
  async resetOrdering() {
    try {
      console.log('🔄 Reset ordine FAQ...');
      const faqs = await getAllFAQs();
      const categories = await getFAQCategories();
      
      // Riorganizza per categoria
      const categoryGroups = new Map<string, typeof faqs>();
      faqs.forEach(faq => {
        if (!categoryGroups.has(faq.category)) {
          categoryGroups.set(faq.category, []);
        }
        categoryGroups.get(faq.category)!.push(faq);
      });
      
      let updatedCount = 0;
      categoryGroups.forEach((groupFAQs, categoryId) => {
        const categoryName = categories.find(c => c.id === categoryId)?.name || categoryId;
        console.log(`\n📝 Sistemando ordine per categoria: ${categoryName}`);
        
        // Ordina per data di creazione
        const sortedFAQs = groupFAQs.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return aTime - bTime;
        });
        
        // Aggiorna l'ordine
        sortedFAQs.forEach((faq, index) => {
          const newOrder = index + 1;
          if (faq.order !== newOrder) {
            console.log(`   - "${faq.question.substring(0, 40)}..." → ordine ${newOrder}`);
            updatedCount++;
          }
        });
      });
      
      console.log(`\n✅ Reset ordine completato: ${updatedCount} FAQ aggiornate`);
      return updatedCount;
    } catch (error) {
      console.error('❌ Errore nel reset ordine:', error);
      throw error;
    }
  }
};

// Esporta per uso globale (console del browser)
if (typeof window !== 'undefined') {
  (window as any).FAQCleanup = FAQCleanupUtils;
  console.log('🔧 FAQCleanup utilities disponibili globalmente!');
  console.log('Comandi disponibili:');
  console.log('- FAQCleanup.checkDuplicates() - Controlla doppioni');
  console.log('- FAQCleanup.removeDuplicates() - Rimuovi doppioni');
  console.log('- FAQCleanup.fullCleanup() - Pulizia completa');
  console.log('- FAQCleanup.showStats() - Mostra statistiche');
  console.log('- FAQCleanup.resetOrdering() - Reset ordine');
} 